import { forwardRef, useEffect, useImperativeHandle, useRef } from "react";
import {
  cancel,
  format,
  type Opts,
  register,
  render,
  type TDate,
} from "timeago.js";
import deLocale from "timeago.js/lib/lang/de";

register("de", deLocale);

export interface TimeAgoProps
  extends Omit<React.HTMLAttributes<HTMLTimeElement>, "datetime"> {
  datetime: TDate;
  live?: boolean;
  locale?: string;
  opts?: Opts;
}

/**
 * A ref-forwarding TimeAgo component that works properly with Ant Design's Tooltip
 * and other components that require ref access.
 */
export const TimeAgo = forwardRef<HTMLTimeElement, TimeAgoProps>(
  ({ datetime, live = true, locale, opts, ...rest }, ref) => {
    const internalRef = useRef<HTMLTimeElement>(null);

    // Expose the internal ref to parent components
    useImperativeHandle(ref, () => internalRef.current as HTMLTimeElement, []);

    useEffect(() => {
      const element = internalRef.current;
      if (!element) return;

      // Cancel any existing interval
      cancel(element);

      if (live) {
        // Set the datetime attribute for the live render
        const dateValue =
          datetime instanceof Date ? datetime.getTime() : datetime;
        element.setAttribute("datetime", String(dateValue));
        render(element, locale, opts);
      }

      return () => {
        cancel(element);
      };
    }, [datetime, live, locale, opts]);

    return (
      <time ref={internalRef} {...rest}>
        {format(datetime, locale, opts)}
      </time>
    );
  },
);

TimeAgo.displayName = "TimeAgo";
